import React from 'react';
import PropTypes from 'prop-types';
import { branch, compose, renderNothing } from 'recompose';
import { connect } from 'react-redux';
import { selectors as i18nSelectors, translator } from 'mage-i18n';
import {
  getMerchantAwareTranslator,
  getMerchantCountry,
  getMerchantCountryCurrency,
  getUserMerchantId,
} from '@/selectors/merchant';
import { getAllCurrencies, getCountries } from '@/selectors/options';
import { getOnboardingTypeFromWidgetCookie } from '@/selectors/widget';
import { defaultContainerPropTypes, formUtils, HorizontalWrapper, MOET_CONSTANTS } from 'moet-core';
import { APP_PATHS, CAPS, FEATURES } from '@/constants';
import { withCapabilityBeforeNext } from '@/flow/common';
import { logEvent } from '@/utils/analytics';
import { BackButton, CallToActionButton, ContainerTemplate } from '@/components';
import { SpacerHorizontal } from '@klarna/bubble-ui';
import { generateCyElemId } from 'moet-test-utils/cypress/utils';

import { ANALYTICS_EVENTS } from './analyticsEvents';
import { CY_BANK_ACCOUNT_CONTAINER_IDS } from './constants';
import { getPersistedBankAccounts } from './selectors';
import { ibanCountryCode } from './ibanParser';

import { useBankAccounts } from './useBankAccounts';
import { usePersistedBankAccounts } from './usePersistedBankAccounts';

import BankAccountStack from './components/BankAccountStack';
import IBANFields from './components/IBANFields';
import LocalFields from './components/LocalFields';
import CanadaFields from './components/CanadaFields';
import { getFeatureByKey } from '@/selectors/featuresConfig';

const {
  ACCOUNT_TYPES,
  COUNTRIES_WITH_BIC,
  COUNTRIES_WITH_LOCAL_BANK_ACCOUNT,
  COUNTRIES_WITH_ROUTING_NUMBER,
  ONBOARDING_TYPE,
} = MOET_CONSTANTS;

const tContainerPrefix = 'products-fe.containers.bank_account';

const BANK_ACCOUNT_STACK_TAB_INDEX_RHYTHM = 10;

const areAllTransientAccountsValid = (accounts, persistedKeys) => {
  return Object.values(accounts)
    .filter(({ key }) => !persistedKeys.includes(key))
    .every(account => account.isValid);
};

export const BankAccounts = ({
  country,
  currencies,
  ensureAccountInMerchantCurrency = true,
  error,
  hasBack,
  isDisabled,
  isLoading,
  merchantCountry,
  merchantCountryCurrency,
  mid,
  onBack,
  onboardingType,
  onNext,
  persistedBankAccounts,
  supportLocalUKBankAccounts,
  supportLocalSEBankAccounts,
  t,
  tMerchantAware,
}) => {
  const {
    autoDefaultAccountKey,
    persistedAccountKeys,
    defaultAccountKey: initialDefaultAccountKey,
    currencyClaims: initialCurrencyClaims,
    accounts: initialBankAccounts,
  } = usePersistedBankAccounts(persistedBankAccounts, {
    merchantCurrency: merchantCountryCurrency,
  });
  const isGBAndShouldUseIban = !supportLocalUKBankAccounts && country === 'GB';
  const isSEAndShouldUseIban = !supportLocalSEBankAccounts && country === 'SE';

  const {
    accounts,
    defaultAccountKey,
    create,
    canCreate,
    currenciesAvailableFor,
    update,
    remove,
  } = useBankAccounts(initialBankAccounts, {
    autoDefaultAccountKey,
    initialDefaultAccountKey,
    initialCurrencyClaims,
    currencies,
    // For local bank accounts we explicitly only allow 1 account
    // For other countries it will be limited by available currencies
    maxAccounts:
      isGBAndShouldUseIban || !COUNTRIES_WITH_LOCAL_BANK_ACCOUNT.includes(country) ? null : 1,
  });

  const tabIndexAtStackEnd = Object.keys(accounts).length * BANK_ACCOUNT_STACK_TAB_INDEX_RHYTHM;
  const tabIndices = {
    backButton: tabIndexAtStackEnd + 2,
    skipButton: tabIndexAtStackEnd + 3,
    callToAction: tabIndexAtStackEnd + 1,
  };

  const handleSkipBankAccountStep = () => {
    this.props.history.push(APP_PATHS.APPLICATION_CONFIRMATION_PATH + history.location.search);
  };

  const allAccountsValid = areAllTransientAccountsValid(accounts, persistedAccountKeys);

  const canSkipStep =
    onboardingType === ONBOARDING_TYPE.MANAGED && persistedBankAccounts.length === 0;

  const handleFormValidityChange = (accountKey, validity) => {
    const { isFormValid } = validity;
    update(accountKey, 'isValid', isFormValid);
  };

  const handleNext = () => {
    const bankAccountsToAdd = Object.values(accounts);
    const bankAccounts = bankAccountsToAdd.map(
      ({ key, accountNumber, bic, routingNumber, accountHolder, currency, id }) => ({
        id,
        type:
          isGBAndShouldUseIban || isSEAndShouldUseIban
            ? ACCOUNT_TYPES.IBAN
            : ACCOUNT_TYPES[country] || ACCOUNT_TYPES.IBAN,
        data: {
          country:
            isGBAndShouldUseIban || isSEAndShouldUseIban
              ? ibanCountryCode(accountNumber)
              : COUNTRIES_WITH_LOCAL_BANK_ACCOUNT.includes(country)
              ? country
              : ibanCountryCode(accountNumber),
          currency,
          accountHolder,
          bic: COUNTRIES_WITH_BIC.includes(country) ? bic : undefined,
          routingNumber:
            isGBAndShouldUseIban || isSEAndShouldUseIban
              ? undefined
              : COUNTRIES_WITH_ROUTING_NUMBER.includes(country)
              ? routingNumber
              : undefined,
          accountNumber,
          isDefault: defaultAccountKey === key,
        },
      }),
    );

    return formUtils.callToActionOnNextWrapper(
      bankAccounts,
      () => {
        onNext({ payload: { params: { mid }, body: bankAccounts } });
      },
      areAllTransientAccountsValid(accounts, persistedAccountKeys),
    );
  };

  const FIELDS = {
    AU: LocalFields,
    CA: CanadaFields,
    GB: supportLocalUKBankAccounts ? LocalFields : IBANFields,
    SE: supportLocalSEBankAccounts ? LocalFields : IBANFields,
    US: LocalFields,
    MX: LocalFields,
    IBAN: IBANFields,
  };

  const CountrySpecificFields = FIELDS[country] || FIELDS.IBAN;

  return (
    <ContainerTemplate
      title={tMerchantAware(`${tContainerPrefix}.title`)}
      subtitle={tMerchantAware(`${tContainerPrefix}.subtitle`)}
      error={error}
      info={country !== 'US' ? t(`${tContainerPrefix}.robot_message_general`) : undefined}
    >
      <BankAccountStack
        canAdd={canCreate()}
        tabIndexRhythm={BANK_ACCOUNT_STACK_TAB_INDEX_RHYTHM}
        onCardAdd={() => create()}
        onCardRemove={key => remove(key)}
        merchantCountry={merchantCountry.key}
        isDisabled={isLoading}
      >
        {Object.entries(accounts).map(([key, account], index) => (
          <CountrySpecificFields
            {...account}
            // For defaultFlow we do not want to force the merchant one specific currency
            // we want to allow them to select any supported currency
            isCurrencyDisabled={
              ensureAccountInMerchantCurrency && account.currency === merchantCountryCurrency
            }
            useAsDefault={key === defaultAccountKey}
            key={key}
            isDisabled={isLoading}
            country={country}
            currencies={currenciesAvailableFor(key)}
            currencyTooltipText={
              account.currency === merchantCountryCurrency
                ? t(
                    // eslint-disable-next-line max-len
                    `${tContainerPrefix}.fields.currency_code.disable_reason.account_in_merchant_currency_required`,
                    {
                      currency: account.currency,
                      merchantCountry: merchantCountry.label,
                    },
                  )
                : undefined
            }
            useAsDefaultTooltipText={t(
              `${tContainerPrefix}.fields.use_as_default_bank_account.tooltip`,
            )}
            onFieldChange={(field, value) => update(key, field, value)}
            onFormValidityChange={validity => handleFormValidityChange(key, validity)}
            currency={account.currency}
            {...generateCyElemId(
              `${CY_BANK_ACCOUNT_CONTAINER_IDS.BANK_ACCOUNT}_${account.currency || index}`,
              window,
            )}
          />
        ))}
      </BankAccountStack>
      <HorizontalWrapper display="inline-flex" alignItems="center" justifyContent="flex-end">
        {hasBack && (
          <>
            <BackButton tabIndex={tabIndices.backButton} onClick={onBack} disabled={isDisabled}>
              {t(`${tContainerPrefix}.buttons.back`)}
            </BackButton>
            <SpacerHorizontal spaceToken="space/300" />
          </>
        )}
        {canSkipStep && (
          <>
            <BackButton
              tabIndex={tabIndices.skipButton}
              onClick={handleSkipBankAccountStep}
              disabled={isDisabled}
            >
              {t(`${tContainerPrefix}.buttons.skip`)}
            </BackButton>
            <SpacerHorizontal spaceToken="space/300" />
          </>
        )}
        <CallToActionButton
          tabIndex={tabIndices.callToAction}
          loading={isLoading}
          onClick={handleNext}
          disabled={isDisabled || !allAccountsValid}
          {...generateCyElemId(CY_BANK_ACCOUNT_CONTAINER_IDS.BTN_SUBMIT, window)}
        >
          {t(`${tContainerPrefix}.buttons.next`)}
        </CallToActionButton>
      </HorizontalWrapper>
    </ContainerTemplate>
  );
};

BankAccounts.propTypes = {
  ...defaultContainerPropTypes,
  language: PropTypes.string.isRequired,
  merchantCountry: PropTypes.object.isRequired,
  merchantCountryCurrency: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  tMerchantAware: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  countries: PropTypes.array,
  currencies: PropTypes.array,
  ensureAccountInMerchantCurrency: PropTypes.bool,
  persistedBankAccounts: PropTypes.array,
  supportLocalSEBankAccounts: PropTypes.func,
  supportLocalUKBankAccounts: PropTypes.func,
};

export default compose(
  connect(state => {
    return {
      t: translator(state),
      tMerchantAware: getMerchantAwareTranslator(state),
      language: i18nSelectors.language(state),
      countries: getCountries(state),
      currencies: getAllCurrencies(state),
      mid: getUserMerchantId(state),
      merchantCountry: getMerchantCountry(state),
      merchantCountryCurrency: getMerchantCountryCurrency(state),
      persistedBankAccounts: getPersistedBankAccounts(state),
      onboardingType: getOnboardingTypeFromWidgetCookie(state),
      supportLocalUKBankAccounts: getFeatureByKey(FEATURES.SUPPORT_LOCAL_UK_BANK_ACCOUNTS)(state),
      supportLocalSEBankAccounts: getFeatureByKey(FEATURES.SUPPORT_LOCAL_SE_BANK_ACCOUNTS)(state),
    };
  }),
  branch(
    ({ countries = false, currencies = false }) =>
      countries.length === 0 && currencies.length === 0,
    renderNothing,
  ),
  withCapabilityBeforeNext(
    {
      cap: CAPS.V2_MERCHANTS_BANK_ACCOUNT_PUT,
    },
    ({ payload }) => {
      if (payload?.body?.length) {
        // eslint-disable-next-line no-array-constructor
        const countryCurrencyPairs = new Array(...payload.body)
          .map(({ data: { country, currency } }) => `${country},${currency}`)
          .join(';');
        logEvent(ANALYTICS_EVENTS.bankAccount, { countryCurrencyPairs });
      }
    },
  ),
)(BankAccounts);
